import { useMemo } from "react";
import { useStore } from "ra-core";

import type { DealStage, LabeledValue, NoteStatus } from "../types";
import {
  defaultConfiguration,
  defaultProjectStages,
  defaultProjectPipelineStatuses,
} from "./defaultConfiguration";

export const CONFIGURATION_STORE_KEY = "app.configuration";

export interface ConfigurationContextValue {
  companySectors: LabeledValue[];
  currency: string;
  projectTypes: LabeledValue[];
  projectPipelineStatuses: string[];
  projectStages: DealStage[];
  /** @deprecated Legacy configuration names retained for stored config compatibility. */
  dealCategories: LabeledValue[];
  /** @deprecated Legacy configuration names retained for stored config compatibility. */
  dealPipelineStatuses: string[];
  /** @deprecated Legacy configuration names retained for stored config compatibility. */
  dealStages: DealStage[];
  noteStatuses: NoteStatus[];
  taskTypes: LabeledValue[];
  title: string;
  darkModeLogo: string;
  lightModeLogo: string;
}

export const useConfigurationContext = () => {
  const [config] = useStore<ConfigurationContextValue>(
    CONFIGURATION_STORE_KEY,
    defaultConfiguration,
  );
  // Merge with defaults so that missing fields in stored config
  // fall back to default values (e.g. when new settings are added)
  return useMemo(() => {
    const merged = { ...defaultConfiguration, ...config };
    const projectTypes =
      config?.projectTypes ??
      config?.dealCategories ??
      defaultConfiguration.projectTypes;
    const configuredStages =
      config?.projectStages ??
      config?.dealStages ??
      defaultConfiguration.projectStages;
    const hasLegacyStage = configuredStages.some((stage) =>
      ["lead", "estimate-prepared", "sold", "oos-dq", "cancelled"].includes(
        stage.value,
      ),
    );
    const projectStages = hasLegacyStage
      ? defaultProjectStages
      : configuredStages;
    const configuredPipelineStatuses =
      config?.projectPipelineStatuses ??
      config?.dealPipelineStatuses ??
      defaultConfiguration.projectPipelineStatuses;
    const projectPipelineStatuses = hasLegacyStage
      ? defaultProjectPipelineStatuses
      : configuredPipelineStatuses;

    return {
      ...merged,
      projectTypes,
      projectStages,
      projectPipelineStatuses,
      dealCategories: config?.dealCategories ?? projectTypes,
      dealStages: config?.dealStages ?? projectStages,
      dealPipelineStatuses:
        config?.dealPipelineStatuses ?? projectPipelineStatuses,
    };
  }, [config]);
};

export const useConfigurationUpdater = () => {
  const [, setConfig] = useStore<ConfigurationContextValue>(
    CONFIGURATION_STORE_KEY,
  );
  return setConfig;
};
